import {useEffect, useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import TicketsService from '../../../../services/ticket.service'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'

type TicketHistoryEntry = {
  id: string
  actor: string
  newActor?: string | null
  oldStatus?: string | null
  newStatus?: string | null
  ticketId: string
  priority?: string | null
  createdAt: string
  requestBody?: string | null
  responseBody?: string | null
}

const TicketHistory = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [history, setHistory] = useState<TicketHistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const loadHistory = async () => {
    if (!id) return
    setLoading(true)
    setError('')
    try {
      const res = await TicketsService.getTicketHistory(id)
      setHistory(res.data || [])
    } catch (err: any) {
      console.error('Failed to load ticket history:', err)
      setError('Failed to load ticket history')
      if (err?.response?.status === 403) {
        setError('You are not allowed to view this ticket history.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <KTCard>
      <KTCardBody>
        <div className='d-flex justify-content-between align-items-center mb-5'>
          <h3>Ticket History</h3>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={() => navigate(`/tickets/${id}`)}
          >
            Back to Details
          </button>
        </div>

        {loading && <div className='text-center py-5'>Loading history...</div>}

        {error && (
          <div className='alert alert-danger mb-5' role='alert'>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {history && history.length > 0 ? (
              <div className='vstack gap-3'>
                {history.map((entry) => (
                  <div key={entry.id} className='border rounded p-4'>
                    <div className='d-flex justify-content-between align-items-start mb-3'>
                      <div className='flex-grow-1'>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <span className='fw-bold'>Actor:</span>
                          <span>{entry.actor}</span>
                          {entry.newActor && entry.newActor !== entry.actor && (
                            <>
                              <span className='text-muted'>→</span>
                              <span className='fw-semibold text-primary'>{entry.newActor}</span>
                            </>
                          )}
                        </div>

                        {(entry.oldStatus || entry.newStatus) && (
                          <div className='d-flex align-items-center gap-2 mb-2'>
                            <span className='fw-bold'>Status:</span>
                            {entry.oldStatus && (
                              <span className='text-muted'>{entry.oldStatus}</span>
                            )}
                            {entry.oldStatus && entry.newStatus && (
                              <span className='text-muted'>→</span>
                            )}
                            {entry.newStatus && (
                              <span className='fw-semibold text-success'>{entry.newStatus}</span>
                            )}
                          </div>
                        )}

                        {entry.priority && (
                          <div className='mb-2'>
                            <span className='fw-bold'>Priority:</span>{' '}
                            <span className='badge badge-light-warning'>{entry.priority}</span>
                          </div>
                        )}

                        {entry.requestBody && (
                          <div className='mb-2'>
                            <div className='fw-bold mb-1'>Request:</div>
                            <div className='text-muted'>{entry.requestBody}</div>
                          </div>
                        )}

                        {entry.responseBody && (
                          <div>
                            <div className='fw-bold mb-1'>Response:</div>
                            <div className='text-muted'>{entry.responseBody}</div>
                          </div>
                        )}
                      </div>
                      <div className='text-end ms-3'>
                        <div className='text-muted small'>
                          {new Date(entry.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center text-muted py-5'>No history available</div>
            )}
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}

export default TicketHistory
